import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import ConfirmModal from '../components/ConfirmModal';
import styles from './TablePage.module.css';

type Scenario = { id: string; context: string; content: string; answer: string; answer_explanation: string; korean: string };
type Synonym = { id: string; scenario_id: string; synonym: string; explanation: string };

const EMPTY_SCENARIO = { context: '', content: '', answer: '', answer_explanation: '', korean: '' };

export default function Concept2Page() {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [synonyms, setSynonyms] = useState<Synonym[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Scenario | null>(null);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: sc }, { data: sy }] = await Promise.all([
      supabase.from('scenarios').select('*').order('context'),
      supabase.from('scenario_synonyms').select('*'),
    ]);
    setScenarios(sc ?? []);
    setSynonyms(sy ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function openCreate() {
    setForm({ ...EMPTY_SCENARIO, synonyms: [{ synonym: '', explanation: '' }] });
    setCreating(true);
  }

  function openEdit(s: Scenario) {
    const synList = synonyms.filter(x => x.scenario_id === s.id).map(x => ({ synonym: x.synonym, explanation: x.explanation }));
    setForm({ ...s, synonyms: synList.length ? synList : [{ synonym: '', explanation: '' }] });
    setCreating(false);
  }

  async function handleSave() {
    const { synonyms: synList, id, ...rest } = form;
    if (creating) {
      const { data } = await supabase.from('scenarios').insert([rest]).select();
      const newId = data?.[0]?.id;
      if (newId && synList.length) {
        const rows = synList.filter((s: any) => s.synonym).map((s: any) => ({ scenario_id: newId, ...s }));
        if (rows.length) await supabase.from('scenario_synonyms').insert(rows);
      }
    } else {
      await supabase.from('scenarios').update(rest).eq('id', id);
      await supabase.from('scenario_synonyms').delete().eq('scenario_id', id);
      const rows = synList.filter((s: any) => s.synonym).map((s: any) => ({ scenario_id: id, ...s }));
      if (rows.length) await supabase.from('scenario_synonyms').insert(rows);
    }
    setForm(null); fetchAll();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await supabase.from('scenario_synonyms').delete().eq('scenario_id', deleteTarget.id);
    await supabase.from('scenarios').delete().eq('id', deleteTarget.id);
    setDeleteTarget(null); fetchAll();
  }

  const filteredScenarios = scenarios.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.context?.toLowerCase().includes(q) ||
      s.answer?.toLowerCase().includes(q) ||
      s.content?.toLowerCase().includes(q) ||
      synonyms.filter(x => x.scenario_id === s.id).some(x => x.synonym?.toLowerCase().includes(q))
    );
  });

  if (loading) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>✏️ Concept 2 — 빈칸 채우기</h1>
          <span className={styles.count}>{scenarios.length}개 시나리오</span>
          <div className={styles.dbBadge}>🗄 scenarios · scenario_synonyms</div>
        </div>
        <div className={styles.actions}>
          <input className={`input ${styles.search}`} placeholder="검색..." value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-ghost" onClick={fetchAll}>↺</button>
          <button className="btn btn-primary" onClick={openCreate}>+ 추가</button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className="data-table">
          <thead><tr><th>Context</th><th>Content (빈칸문장)</th><th>Answer</th><th>유의어 수</th><th style={{width:100}}>Actions</th></tr></thead>
          <tbody>
            {filteredScenarios.map(s => {
              const synList = synonyms.filter(x => x.scenario_id === s.id);
              const isExpanded = expanded === s.id;
              return (
                <React.Fragment key={s.id}>
                  <tr style={{cursor:'pointer'}} onClick={() => setExpanded(isExpanded ? null : s.id)}>
                    <td title={s.context}>{s.context}</td>
                    <td title={s.content}>{s.content}</td>
                    <td><strong>{s.answer}</strong></td>
                    <td>
                      <span style={{display:'flex',alignItems:'center',gap:6}}>
                        {synList.length}개
                        <span style={{color:'rgba(255,255,255,0.3)',fontSize:11}}>{isExpanded ? '▲' : '▼'}</span>
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button className="btn btn-ghost" style={{padding:'4px 10px',fontSize:12}} onClick={e => { e.stopPropagation(); openEdit(s); }}>편집</button>
                        <button className="btn btn-danger" style={{padding:'4px 10px',fontSize:12}} onClick={e => { e.stopPropagation(); setDeleteTarget(s); }}>삭제</button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={5} style={{padding:'0 0 0 16px', background:'rgba(255,255,255,0.04)'}}>
                        <div style={{padding:'12px 16px'}}>
                          <div style={{fontSize:10,fontWeight:700,letterSpacing:1.2,color:'rgba(255,255,255,0.4)',marginBottom:10,textTransform:'uppercase'}}>
                            유의어 목록
                          </div>
                          {synList.length === 0 ? (
                            <div style={{color:'rgba(255,255,255,0.3)',fontSize:13}}>유의어 없음</div>
                          ) : (
                            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                              {synList.map(syn => (
                                <div key={syn.id} style={{
                                  background:'rgba(155,127,212,0.2)',
                                  border:'1px solid rgba(155,127,212,0.4)',
                                  borderRadius:10,padding:'6px 14px',
                                }}>
                                  <div style={{fontSize:13,fontWeight:700,color:'white'}}>{syn.synonym}</div>
                                  {syn.explanation && (
                                    <div style={{fontSize:11,color:'rgba(255,255,255,0.5)',marginTop:2}}>{syn.explanation}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="modal-overlay" onClick={() => setForm(null)}>
          <div className="modal-box" style={{width:600,maxHeight:'90vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
              <h2 style={{fontSize:18,fontWeight:800}}>시나리오 {creating ? '추가' : '편집'}</h2>
              <button className="btn btn-ghost" style={{padding:'4px 8px'}} onClick={() => setForm(null)}>✕</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              {[
                {key:'context', label:'Context (상황 설명)'},
                {key:'content', label:'Content (빈칸은 ___)'},
                {key:'korean', label:'Korean (해석)'},
                {key:'answer', label:'Answer (정답)'},
                {key:'answer_explanation', label:'Answer Explanation'},
              ].map(({key, label}) => (
                <div key={key}>
                  <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:6}}>{label}</label>
                  <textarea className="input" rows={key==='content'||key==='answer_explanation'?2:1} style={{resize:'vertical'}}
                    value={form[key] ?? ''} onChange={e => setForm((p: any) => ({...p, [key]: e.target.value}))} />
                </div>
              ))}
              <div>
                <label style={{fontSize:11,fontWeight:700,color:'rgba(255,255,255,0.6)',letterSpacing:1,display:'block',marginBottom:10}}>유의어</label>
                {form.synonyms.map((s: any, i: number) => (
                  <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'flex-start'}}>
                    <div style={{flex:1}}>
                      <input className="input" placeholder="유의어 단어" value={s.synonym}
                        onChange={e => setForm((p: any) => { const arr=[...p.synonyms]; arr[i]={...arr[i],synonym:e.target.value}; return {...p,synonyms:arr}; })} />
                    </div>
                    <div style={{flex:2}}>
                      <input className="input" placeholder="설명" value={s.explanation}
                        onChange={e => setForm((p: any) => { const arr=[...p.synonyms]; arr[i]={...arr[i],explanation:e.target.value}; return {...p,synonyms:arr}; })} />
                    </div>
                    <button className="btn btn-danger" style={{padding:'8px 10px',fontSize:12,marginTop:0}}
                      onClick={() => setForm((p: any) => ({...p, synonyms: p.synonyms.filter((_: any, j: number) => j!==i)}))}>✕</button>
                  </div>
                ))}
                <button className="btn btn-ghost" style={{fontSize:12}}
                  onClick={() => setForm((p: any) => ({...p, synonyms: [...p.synonyms, {synonym:'',explanation:''}]}))}>
                  + 유의어 추가
                </button>
              </div>
            </div>
            <div style={{display:'flex',gap:10,marginTop:24,justifyContent:'flex-end'}}>
              <button className="btn btn-ghost" onClick={() => setForm(null)}>취소</button>
              <button className="btn btn-primary" onClick={handleSave}>저장</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal title="시나리오 삭제" message={`"${deleteTarget.context}"를 삭제할까요?`}
          onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
  );
}
